import { useState } from 'react';
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Type,
  FileText,
  User,
  Calendar,
  Flag,
  SortAsc
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

export type SortField = 'title' | 'description' | 'createdBy' | 'datetime' | 'importance';
export type SortDirection = 'asc' | 'desc';

export interface SortOption {
  field: SortField;
  direction: SortDirection;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

export interface ContentSortSelectProps {
  value?: { field: SortField; direction: SortDirection };
  onValueChange?: (value: { field: SortField; direction: SortDirection }) => void;
  disabled?: boolean;
  className?: string;
}

const sortOptions: SortOption[] = [
  {
    field: 'title',
    direction: 'asc',
    label: 'Title A-Z',
    icon: Type,
  },
  {
    field: 'title',
    direction: 'desc',
    label: 'Title Z-A',
    icon: Type,
  },
  {
    field: 'description',
    direction: 'asc',
    label: 'Description A-Z',
    icon: FileText,
  },
  {
    field: 'description',
    direction: 'desc',
    label: 'Description Z-A',
    icon: FileText,
  },
  {
    field: 'createdBy',
    direction: 'asc',
    label: 'Creator A-Z',
    icon: User,
  },
  {
    field: 'createdBy',
    direction: 'desc',
    label: 'Creator Z-A',
    icon: User,
  },
  {
    field: 'datetime',
    direction: 'asc',
    label: 'Old to New',
    icon: Calendar,
  },
  {
    field: 'datetime',
    direction: 'desc',
    label: 'New to Old',
    icon: Calendar,
  },
  // Ascending only: lower = more important, so no desc twin.
  {
    field: 'importance',
    direction: 'asc',
    label: 'Importance',
    icon: Flag,
  },
];

export function ContentSortSelect({
  value = { field: 'datetime', direction: 'desc' }, // Default to newest first
  onValueChange,
  disabled = false,
  className,
}: ContentSortSelectProps) {
  const [open, setOpen] = useState(false);

  const currentOption = sortOptions.find(
    option => option.field === value.field && option.direction === value.direction
  );

  const handleOptionSelect = (option: SortOption) => {
    onValueChange?.({ field: option.field, direction: option.direction });
    setOpen(false);
  };

  const getDirectionIcon = (direction: SortDirection) => {
    return direction === 'asc' ? ArrowUp : ArrowDown;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <div
              className={cn(
                "flex items-center gap-2 h-8 cursor-pointer hover:text-foreground text-sm text-muted-foreground",
                disabled && "opacity-50 cursor-not-allowed",
                className
              )}
              onClick={disabled ? undefined : () => setOpen(!open)}
            >
              <ArrowUpDown className="h-4 w-4" />
              <span>Sort</span>
            </div>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>
          <p>Sort by: {currentOption?.label || 'New to Old'}</p>
        </TooltipContent>
      </Tooltip>
      
      <PopoverContent 
        className="w-64 p-2"
        align="start"
        side="bottom"
        sideOffset={8}
      >
        <div className="space-y-1">
          <div className="px-2 py-1.5 text-sm font-medium text-muted-foreground">
            Sort by
          </div>
          
          {sortOptions.map((option) => {
            const IconComponent = option.icon;
            const DirectionIcon = getDirectionIcon(option.direction);
            const isSelected = currentOption?.field === option.field && currentOption?.direction === option.direction;
            
            return (
              <div
                key={`${option.field}-${option.direction}`}
                className={cn(
                  "flex items-center gap-3 px-2 py-2 rounded-md cursor-pointer transition-colors text-sm",
                  "hover:bg-accent hover:text-accent-foreground",
                  isSelected && "bg-accent text-accent-foreground"
                )}
                onClick={() => handleOptionSelect(option)}
              >
                <div className="flex items-center gap-2 flex-1">
                  <IconComponent className="h-4 w-4 text-muted-foreground" />
                  <span>{option.label}</span>
                </div>
                <DirectionIcon className="h-4 w-4 text-muted-foreground" />
              </div>
            );
          })}
        </div>
        
        {/* Current selection indicator */}
        {currentOption && (
          <div className="mt-3 pt-2 border-t">
            <div className="flex items-center gap-2 px-2 text-xs text-muted-foreground">
              <SortAsc className="h-3 w-3" />
              <span>Currently: {currentOption.label}</span>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
